// src/features/estoque/components/FilaNotasImportacao.jsx
import React, { useState, useEffect } from "react";
import { db } from "services/firebaseConfig";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "components/ui/card";
import { Button } from "components/ui/button";
import { parseNotaFiscalXML } from "utils/xmlParser";
import { atualizarEstoqueViaXML, observarEstoque } from "services/estoqueService";
import ModalMapeamentoNota from "./ModalMapeamentoNota";
import DicionarioMapeamentoModal from "./DicionarioMapeamentoModal";
import { ModalCertificadoSefaz } from "./ModalCertificadoSefaz";
import {
  Smartphone,
  Copy,
  ExternalLink,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Zap,
  Play,
  Radio,
  Upload,
  FileText,
  Check,
  Tag,
  BookOpen,
  Key,
} from "lucide-react";
import toast from "react-hot-toast";

// URLs dos serviços SEFAZ (Nuvem em reservas.parquetimbo.com.br ou Agente Local no PC)
const REMOTE_SEFAZ_URL = process.env.REACT_APP_SEFAZ_URL || "https://reservas.parquetimbo.com.br";
const LOCAL_AGENT_URL = "http://localhost:4040";

/**
 * Extrai informações legíveis e humanas da Chave de Acesso da NF-e (44 dígitos)
 */
function extrairInfoChave(chave) {
  if (!chave || chave.length < 44) {
    return {
      modelo: "Nota",
      numero: null,
      serie: null,
      mesAno: null,
      finalChave: chave ? chave.slice(-6) : "",
    };
  }

  const mod = chave.substring(20, 22);
  const serie = parseInt(chave.substring(22, 25), 10);
  const nNF = parseInt(chave.substring(25, 34), 10);
  const mes = chave.substring(4, 6);
  const ano = "20" + chave.substring(2, 4);

  return {
    modelo: mod === "65" ? "NFC-e" : "NF-e",
    numero: isNaN(nNF) ? null : nNF.toLocaleString("pt-BR"),
    serie: isNaN(serie) ? 1 : serie,
    mesAno: `${mes}/${ano}`,
    finalChave: chave.slice(-8).replace(/(.{4})/g, "$1 ").trim(),
  };
}

/**
 * Normaliza o status para garantir que notas já manifestadas com protocolo
 * recebam o badge correto de Ciência Homologada e não fiquem em erro vermelho
 */
function normalizarStatus(item) {
  if (item.status === "sucesso") return "sucesso";
  if (item.status === "processando") return "processando";
  if (item.status === "aguardando_mapeamento") return "aguardando_mapeamento";
  if (
    item.status === "ciencia_registrada" ||
    (item.mensagem_erro && item.mensagem_erro.includes("Ciência da Emissão"))
  ) {
    return "ciencia_registrada";
  }
  if (item.status === "erro") return "erro";
  return "pendente";
}

const FilaNotasImportacao = () => {
  const [fila, setFila] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const [copiedChave, setCopiedChave] = useState(null);
  const [agentStatus, setAgentStatus] = useState({
    isOnline: false,
    isRemote: false,
    isLocal: false,
    certConfigured: false,
    certExists: false,
    checked: false,
  });

  // Estado para Certificado Digital A1 em sessão
  const [sessaoCertificado, setSessaoCertificado] = useState(() => {
    try {
      const saved = sessionStorage.getItem("sefaz_cert_session");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [modalCertAberto, setModalCertAberto] = useState(false);
  const [notaIndividualParaBaixar, setNotaIndividualParaBaixar] = useState(null);

  // Estados para Mapeamento de Bebidas e Dicionário /expert
  const [estoqueItems, setEstoqueItems] = useState([]);
  const [modalMapeamentoAberto, setModalMapeamentoAberto] = useState(false);
  const [notaParaMapear, setNotaParaMapear] = useState(null);
  const [modalDicionarioAberto, setModalDicionarioAberto] = useState(false);

  // Escuta o estoque do parque em tempo real
  useEffect(() => {
    const unsub = observarEstoque((itens) => {
      setEstoqueItems(itens);
    });
    return () => unsub();
  }, []);

  // 1. Escuta a fila de importação em tempo real no Firestore
  useEffect(() => {
    const q = query(
      collection(db, "fila_importacao_nfe"),
      orderBy("criado_em", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const itens = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          itens.push({
            id: docSnap.id,
            ...data,
            criado_em: data.criado_em?.toDate
              ? data.criado_em.toDate()
              : new Date(),
          });
        });
        setFila(itens);
        setIsLoading(false);
      },
      (error) => {
        console.error("Erro ao escutar fila de importação:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // 2. Verifica a saúde dos serviços SEFAZ (Nuvem em reservas.parquetimbo.com.br e Local)
  useEffect(() => {
    let isMounted = true;

    const checkAgentHealth = async () => {
      let remoteOk = false;
      let localOk = false;
      let localCertConfigured = false;
      let localCertExists = false;

      // 1. Checa serviço no provedor
      try {
        const cRemote = new AbortController();
        const tRemote = setTimeout(() => cRemote.abort(), 2500);
        const resRemote = await fetch(`${REMOTE_SEFAZ_URL}/health`, {
          method: "GET",
          signal: cRemote.signal,
        });
        clearTimeout(tRemote);
        if (resRemote.ok) {
          const dataRemote = await resRemote.json();
          if (dataRemote.status === "online") {
            remoteOk = true;
          }
        }
      } catch (e) {}

      // 2. Checa agente local (localhost:4040)
      try {
        const cLocal = new AbortController();
        const tLocal = setTimeout(() => cLocal.abort(), 1500);
        const resLocal = await fetch(`${LOCAL_AGENT_URL}/health`, {
          method: "GET",
          signal: cLocal.signal,
        });
        clearTimeout(tLocal);
        if (resLocal.ok) {
          const dataLocal = await resLocal.json();
          if (dataLocal.status === "online") {
            localOk = true;
            localCertConfigured = dataLocal.configured;
            localCertExists = dataLocal.exists;
          }
        }
      } catch (e) {}

      if (isMounted) {
        setAgentStatus({
          isOnline: remoteOk || localOk,
          isRemote: remoteOk,
          isLocal: localOk,
          certConfigured: localCertConfigured,
          certExists: localCertExists,
          checked: true,
        });
      }
    };

    checkAgentHealth();
    const interval = setInterval(checkAgentHealth, 8000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleCopyChave = (chave) => {
    navigator.clipboard.writeText(chave);
    setCopiedChave(chave);
    toast.success("Chave de 44 dígitos copiada!", { icon: "📋" });
    setTimeout(() => setCopiedChave(null), 3000);
  };

  const handleAbrirPortalComChave = (chave) => {
    navigator.clipboard.writeText(chave);
    setCopiedChave(chave);
    toast.success("Chave copiada! No portal da SEFAZ, basta pressionar Ctrl+V.", {
      duration: 5000,
      icon: "📋",
    });
    window.open(
      "https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=resumo&tipoConteudo=7PhJ%20gAVw2g=",
      "_blank"
    );
  };

  const handleDeleteItem = async (id) => {
    try {
      await deleteDoc(doc(db, "fila_importacao_nfe", id));
      toast.success("Nota removida da fila.");
    } catch (err) {
      toast.error("Erro ao remover nota.");
    }
  };

  const handleUploadManualXML = async (event, item) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const xmlText = await file.text();
      const parsed = parseNotaFiscalXML(xmlText);
      if (!parsed.success) {
        throw new Error(parsed.error || "Arquivo XML inválido.");
      }

      const resultadoEstoque = await atualizarEstoqueViaXML(parsed);
      if (!resultadoEstoque.success) {
        if (resultadoEstoque.pendingMapping) {
          await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
            status: "aguardando_mapeamento",
            mensagem_erro: "Bebida nova detectada. Mapeamento necessário.",
            itens_pendentes_mapeamento: resultadoEstoque.failedItems,
            cnpj_emitente: parsed.cnpjEmitente,
            nome_emitente: parsed.nomeEmitente || "",
            arquivo_origem: file.name,
          });

          // Abre imediatamente o modal para o usuário mapear sem travar!
          setNotaParaMapear({
            ...item,
            itens_pendentes_mapeamento: resultadoEstoque.failedItems,
            parsedData: parsed,
            cnpjEmitente: parsed.cnpjEmitente,
            nomeEmitente: parsed.nomeEmitente || "",
            arquivo_origem: file.name,
          });
          setModalMapeamentoAberto(true);
          toast(`Nota aguardando mapeamento de bebidas.`, { icon: "⚠️" });
          return;
        }
        throw new Error(resultadoEstoque.message);
      }

      await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
        status: "sucesso",
        mensagem_erro: null,
        processado_em: new Date(),
        arquivo_origem: file.name,
      });

      toast.success("XML importado e estoque atualizado com sucesso!", { icon: "🎉" });
    } catch (err) {
      console.error("Erro ao importar XML manual:", err);
      toast.error(`Falha ao importar XML: ${err.message}`);
    }
  };

  const handleAbrirModalMapeamento = async (item) => {
    // 1. Se os itens já estiverem salvos no documento da fila
    if (item.itens_pendentes_mapeamento && item.itens_pendentes_mapeamento.length > 0) {
      setNotaParaMapear(item);
      setModalMapeamentoAberto(true);
      return;
    }

    // 2. Tentar recuperar o XML via serviço SEFAZ
    const toastId = toast.loading("Carregando produtos da nota fiscal...");
    try {
      const data = await executarDownloadNota(item.chave_acesso, sessaoCertificado);

      if (data && data.success && data.xml) {
        const parsed = parseNotaFiscalXML(data.xml);
        if (parsed.success) {
          const resEstoque = await atualizarEstoqueViaXML(parsed);
          if (resEstoque.pendingMapping) {
            await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
              itens_pendentes_mapeamento: resEstoque.failedItems,
              cnpj_emitente: parsed.cnpjEmitente,
              nome_emitente: parsed.nomeEmitente || "",
            });
            setNotaParaMapear({
              ...item,
              itens_pendentes_mapeamento: resEstoque.failedItems,
              parsedData: parsed,
              cnpjEmitente: parsed.cnpjEmitente,
              nomeEmitente: parsed.nomeEmitente || "",
            });
            setModalMapeamentoAberto(true);
            toast.dismiss(toastId);
            return;
          } else if (resEstoque.success) {
            await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
              status: "sucesso",
              mensagem_erro: null,
              processado_em: new Date(),
            });
            toast.dismiss(toastId);
            toast.success("Todos os itens desta nota já estão cadastrados! Estoque atualizado.", { icon: "🎉" });
            return;
          }
        }
      } else if (data?.message) {
        toast.dismiss(toastId);
        toast.error(`SEFAZ: ${data.message}`, { duration: 5000 });
        return;
      }
    } catch (e) {
      console.log("Erro ao consultar XML no serviço SEFAZ:", e);
    }

    toast.dismiss(toastId);
    toast.error("Não foi possível obter o XML automaticamente. Use o botão 'Subir XML' nesta nota.", {
      duration: 5000,
    });
  };

  const handleSalvarMapeamentoSucesso = async () => {
    if (!notaParaMapear) return;
    const item = notaParaMapear;

    try {
      // Se temos o parsedData em memória
      if (item.parsedData) {
        const resFinal = await atualizarEstoqueViaXML(item.parsedData);
        if (resFinal.success) {
          await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
            status: "sucesso",
            mensagem_erro: null,
            processado_em: new Date(),
          });
          toast.success("Mapeamento concluído e estoque atualizado!", { icon: "🎉" });
          return;
        }
      }

      // Se o agente local está online, re-executa a nota
      if (agentStatus.isOnline) {
        const resp = await fetch(
          `${LOCAL_AGENT_URL}/api/nfe/consultar-e-baixar?chave=${item.chave_acesso}`
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.xml) {
            const parsed = parseNotaFiscalXML(data.xml);
            const resFinal = await atualizarEstoqueViaXML(parsed);
            if (resFinal.success) {
              await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
                status: "sucesso",
                mensagem_erro: null,
                processado_em: new Date(),
              });
              toast.success("Mapeamento concluído e estoque atualizado!", { icon: "🎉" });
              return;
            }
          }
        }
      }

      // Fallback: volta status para pendente para nova tentativa
      await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
        status: "pendente",
        mensagem_erro: null,
      });
      toast.success("Mapeamento registrado! Nota pronta para processamento.");
    } catch (err) {
      console.error("Erro pós mapeamento:", err);
      toast.error(`Mapeamento salvo, mas erro ao dar entrada: ${err.message}`);
    }
  };

  const handleLimparConcluidas = async () => {
    const concluidas = fila.filter((f) => f.status === "sucesso");
    if (concluidas.length === 0) return;

    try {
      const batch = writeBatch(db);
      concluidas.forEach((item) => {
        batch.delete(doc(db, "fila_importacao_nfe", item.id));
      });
      await batch.commit();
      toast.success(`${concluidas.length} nota(s) concluída(s) removida(s).`);
    } catch (err) {
      toast.error("Erro ao limpar concluídas.");
    }
  };

  // Função central para executar o download na SEFAZ (Nuvem ou Local)
  const executarDownloadNota = async (chave, certDados = null) => {
    const urlsToTry = [];
    if (agentStatus.isLocal) {
      urlsToTry.push(LOCAL_AGENT_URL);
    }
    urlsToTry.push(REMOTE_SEFAZ_URL);
    if (!urlsToTry.includes(LOCAL_AGENT_URL)) {
      urlsToTry.push(LOCAL_AGENT_URL);
    }

    let lastError = null;
    for (const baseUrl of urlsToTry) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 45000);

        const resp = await fetch(`${baseUrl}/baixar-xml`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            chave,
            pfxBase64: certDados?.pfxBase64 || undefined,
            senha: certDados?.senha || undefined,
          }),
        });
        clearTimeout(timeoutId);

        if (resp.ok) {
          const contentType = resp.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data = await resp.json();
            return data;
          } else {
            throw new Error(`O servidor em ${baseUrl} retornou página web (HTML) em vez da resposta da SEFAZ. O Node.js precisa ser ativado no Plesk.`);
          }
        } else {
          const errData = await resp.json().catch(() => ({}));
          if (errData.message) {
            throw new Error(errData.message);
          }
        }
      } catch (err) {
        let msg = err.message;
        if (msg === "Failed to fetch") {
          msg = `Servidor SEFAZ offline em ${baseUrl}. No PC, execute 'iniciar-agente-sefaz.bat', ou ative o Node.js no Plesk.`;
        }
        lastError = new Error(msg);
        console.warn(`[SEFAZ] Falha ao tentar ${baseUrl}: ${msg}`);
      }
    }
    throw lastError || new Error("Não foi possível conectar ao serviço SEFAZ no servidor.");
  };

  // Dispara o download em lote ou abre o modal caso o certificado ainda não esteja disponível
  const handleIniciarDownloadSefaz = () => {
    const pendentes = fila.filter((f) => {
      const s = normalizarStatus(f);
      return s === "pendente" || s === "erro" || s === "ciencia_registrada";
    });

    if (pendentes.length === 0) {
      toast("Nenhuma nota pendente para processar.", { icon: "ℹ️" });
      return;
    }

    setNotaIndividualParaBaixar(null);

    // Se já tem certificado em sessão ou agente local configurado
    if (sessaoCertificado?.pfxBase64 || (agentStatus.isLocal && agentStatus.certConfigured)) {
      executarProcessamentoComCertificado(sessaoCertificado, pendentes);
    } else {
      setModalCertAberto(true);
    }
  };

  // Callback chamado quando o usuário submete o modal de certificado
  const handleConfirmarCertificado = (dadosCert) => {
    if (dadosCert.lembrarSessao) {
      setSessaoCertificado(dadosCert);
      try {
        sessionStorage.setItem(
          "sefaz_cert_session",
          JSON.stringify({
            pfxBase64: dadosCert.pfxBase64,
            senha: dadosCert.senha,
            nomeArquivo: dadosCert.nomeArquivo,
          })
        );
      } catch (e) {}
    }
    setModalCertAberto(false);

    const itensParaProcessar = notaIndividualParaBaixar
      ? [notaIndividualParaBaixar]
      : fila.filter((f) => {
          const s = normalizarStatus(f);
          return s === "pendente" || s === "erro" || s === "ciencia_registrada";
        });

    setNotaIndividualParaBaixar(null);
    executarProcessamentoComCertificado(dadosCert, itensParaProcessar);
  };

  const handleLimparSessaoCertificado = () => {
    setSessaoCertificado(null);
    try {
      sessionStorage.removeItem("sefaz_cert_session");
    } catch (e) {}
    toast("Certificado removido da sessão desta aba.", { icon: "ℹ️" });
  };

  // 3. Processar lista de notas com o certificado fornecido
  const executarProcessamentoComCertificado = async (dadosCert, listaItens) => {
    if (!listaItens || listaItens.length === 0) return;

    setIsProcessingBatch(true);
    let processadasCount = 0;

    for (const item of listaItens) {
      try {
        await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
          status: "processando",
          mensagem_erro: null,
        });

        const data = await executarDownloadNota(item.chave_acesso, dadosCert);

        // Se a nota está na fila assíncrona da SEFAZ
        if (!data.success) {
          if (data.aguardandoConsolidacao) {
            await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
              status: "ciencia_registrada",
              mensagem_erro: data.message,
              protocolo_sefaz: data.nProt || null,
              manifestado_em: new Date(),
            });
            toast(
              `Ciência homologada na SEFAZ (Prot: ${data.nProt || "OK"}). Aguardando consolidação.`,
              { icon: "🛡️", duration: 5000 }
            );
            continue;
          }
          throw new Error(data.message || "SEFAZ não retornou o XML desta nota.");
        }

        if (!data.xml) {
          throw new Error("Resposta da SEFAZ não continha o XML da nota fiscal.");
        }

        // Faz o parse do XML
        const parsed = parseNotaFiscalXML(data.xml);
        if (!parsed.success) {
          throw new Error(parsed.error || "Estrutura do XML inválida.");
        }

        // Atualiza o estoque
        const resultadoEstoque = await atualizarEstoqueViaXML(parsed);

        if (!resultadoEstoque.success) {
          if (resultadoEstoque.pendingMapping) {
            await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
              status: "aguardando_mapeamento",
              mensagem_erro: "Bebida nova detectada. Mapeamento necessário.",
              itens_pendentes_mapeamento: resultadoEstoque.failedItems,
              cnpj_emitente: parsed.cnpjEmitente,
              nome_emitente: parsed.nomeEmitente || "",
            });
            toast(
              `Nota ...${item.chave_acesso.slice(-6)} aguardando mapeamento de bebidas.`,
              { icon: "⚠️" }
            );
            continue;
          }
          throw new Error(resultadoEstoque.message);
        }

        // Marca como concluído com sucesso
        await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
          status: "sucesso",
          mensagem_erro: null,
          protocolo_sefaz: data.nProt || null,
          processado_em: new Date(),
        });

        processadasCount++;
      } catch (err) {
        console.error(`Erro ao processar chave ${item.chave_acesso}:`, err);
        await updateDoc(doc(db, "fila_importacao_nfe", item.id), {
          status: "erro",
          mensagem_erro: err.message,
        });
      }
    }

    setIsProcessingBatch(false);
    if (processadasCount > 0) {
      toast.success(
        `${processadasCount} nota(s) baixada(s) e estoque atualizado com sucesso!`,
        { icon: "🎉", duration: 5000 }
      );
    }
  };

  const getStatusBadge = (statusNormalizado) => {
    switch (statusNormalizado) {
      case "sucesso":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-500 dark:text-emerald-400 border border-emerald-500/30">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Importada
          </span>
        );
      case "ciencia_registrada":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-sky-500/15 text-sky-500 dark:text-sky-400 border border-sky-500/30">
            <ShieldCheck className="w-3.5 h-3.5" />
            Ciência Homologada
          </span>
        );
      case "processando":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-500/15 text-indigo-500 dark:text-indigo-400 border border-indigo-500/30">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Consultando SEFAZ
          </span>
        );
      case "aguardando_mapeamento":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/15 text-amber-500 dark:text-amber-400 border border-amber-500/30">
            <AlertCircle className="w-3.5 h-3.5" />
            Mapear Insumo
          </span>
        );
      case "erro":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-500/15 text-rose-500 dark:text-rose-400 border border-rose-500/30">
            <AlertCircle className="w-3.5 h-3.5" />
            Aguardando SEFAZ
          </span>
        );
      case "pendente":
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/15 text-amber-500 dark:text-amber-400 border border-amber-500/30">
            <Clock className="w-3.5 h-3.5" />
            Pronta p/ Baixar
          </span>
        );
    }
  };

  const pendentesCount = fila.filter((f) => {
    const s = normalizarStatus(f);
    return s === "pendente" || s === "erro" || s === "ciencia_registrada";
  }).length;

  const concluidasCount = fila.filter((f) => f.status === "sucesso").length;

  return (
    <Card className="border-gray-200 dark:border-border overflow-hidden">
      <CardHeader className="p-4 sm:p-5 pb-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 rounded-lg text-emerald-600 dark:text-emerald-400">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base sm:text-lg font-bold">
                  Fila de Sincronização
                </CardTitle>
                {fila.length > 0 && (
                  <span className="text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                    {fila.length} nota{fila.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <CardDescription className="text-xs">
                Sincronizado em tempo real entre celular e computador.
              </CardDescription>
            </div>
          </div>

          {/* Status do Agente Local e Ferramentas */}
          <div className="flex flex-wrap items-center gap-2 self-stretch sm:self-auto justify-start sm:justify-end">
            {sessaoCertificado ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-semibold whitespace-nowrap shrink-0 shadow-sm">
                <ShieldCheck className="w-3.5 h-3.5" />
                Certificado Ativo
              </span>
            ) : agentStatus.isOnline ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-semibold whitespace-nowrap shrink-0 shadow-sm">
                <Radio className="w-3.5 h-3.5 animate-pulse" />
                {agentStatus.isRemote ? "SEFAZ Nuvem Online" : "Agente SEFAZ Online"}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setModalCertAberto(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-emerald-500/50 text-xs font-semibold whitespace-nowrap shrink-0 cursor-pointer transition-colors"
                title="Clique para anexar o Certificado A1 (.pfx)"
              >
                <Key className="w-3.5 h-3.5 text-emerald-500" />
                Anexar Certificado A1
              </button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalDicionarioAberto(true)}
              className="text-xs h-8 px-3 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 flex items-center gap-1.5 whitespace-nowrap shrink-0 shadow-sm"
              title="Abrir dicionário de conversões e auditoria de notas"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Dicionário /expert</span>
            </Button>

            {concluidasCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLimparConcluidas}
                className="text-xs h-8 px-3 whitespace-nowrap shrink-0 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Limpar Concluídas
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-5 pt-0">
        {/* Banner de Ação Rápida */}
        <div
          className={`mb-3.5 border rounded-xl p-3 sm:p-3.5 flex flex-col gap-2.5 transition-colors ${
            sessaoCertificado || agentStatus.isOnline
              ? "bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-500/30 text-emerald-900 dark:text-emerald-200 shadow-sm"
              : "bg-slate-50 dark:bg-slate-900/60 border-slate-200/90 dark:border-slate-800 text-slate-700 dark:text-slate-300 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs min-w-0 flex-1">
              <ShieldCheck
                className={`w-4 h-4 shrink-0 ${
                  sessaoCertificado || agentStatus.isOnline
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              />
              <span className="font-semibold truncate">
                {sessaoCertificado ? (
                  <>
                    Certificado A1: <span className="font-mono text-emerald-700 dark:text-emerald-300">{sessaoCertificado.nomeArquivo || "certificado.pfx"}</span>
                  </>
                ) : agentStatus.isOnline ? (
                  "Serviço SEFAZ conectado e pronto."
                ) : (
                  "Clique abaixo para informar o Certificado A1."
                )}
              </span>
            </div>

            {sessaoCertificado && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLimparSessaoCertificado}
                className="text-[11px] h-6 px-2 text-slate-500 hover:text-rose-600 hover:bg-rose-500/10 shrink-0 font-medium"
                title="Remover certificado da memória da sessão"
              >
                Trocar
              </Button>
            )}
          </div>

          {pendentesCount > 0 && (
            <Button
              onClick={handleIniciarDownloadSefaz}
              disabled={isProcessingBatch}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs h-9 px-4 rounded-lg shadow-sm flex items-center justify-center gap-1.5"
            >
              {isProcessingBatch ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Consultando SEFAZ...
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
                  Baixar na SEFAZ ({pendentesCount})
                </>
              )}
            </Button>
          )}
        </div>

        {/* Lista de Itens Mobile-Friendly */}
        {isLoading ? (
          <div className="text-center py-6 text-slate-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            Carregando fila de notas...
          </div>
        ) : fila.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-slate-500 dark:text-slate-400 text-sm flex flex-col items-center justify-center p-4">
            <Zap className="w-6 h-6 text-slate-400 mb-1" />
            <span className="font-medium">Nenhuma nota pendente na fila.</span>
            <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
              Toque em "Bipar Notas pelo Celular" para escanear com a câmera.
            </span>
          </div>
        ) : (
          <div className="space-y-2.5 max-h-[460px] overflow-y-auto pr-0.5">
            {fila.map((item) => {
              const info = extrairInfoChave(item.chave_acesso);
              const statusNorm = normalizarStatus(item);
              const isCopied = copiedChave === item.chave_acesso;

              return (
                <div
                  key={item.id}
                  className="bg-slate-50/70 dark:bg-card/90 border border-slate-200 dark:border-slate-800 rounded-xl p-3 sm:p-3.5 shadow-sm space-y-2.5 transition-all hover:border-slate-300 dark:hover:border-slate-700"
                >
                  {/* Linha 1: Título Legível da Nota e Badge */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 leading-tight truncate">
                            {info.modelo} nº {info.numero || "Documento"}
                          </h4>
                          {info.serie && (
                            <span className="text-[10px] font-medium bg-slate-200/70 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded">
                              Série {info.serie}
                            </span>
                          )}
                        </div>

                        {/* Chave final com botão de copiar */}
                        <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 font-mono mt-0.5 whitespace-nowrap">
                          <span>Final:</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                            {info.finalChave}
                          </span>
                          <button
                            onClick={() => handleCopyChave(item.chave_acesso)}
                            className="p-1 text-slate-400 hover:text-emerald-500 transition-colors shrink-0"
                            title="Copiar chave completa (44 dígitos)"
                          >
                            {isCopied ? (
                              <Check className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0">
                      {getStatusBadge(statusNorm)}
                    </div>
                  </div>

                  {/* Linha 2: Status explicativo e detalhes de captura */}
                  <div className="flex flex-col gap-1 text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-200/60 dark:border-slate-800/80 pt-2">
                    {/* Mensagem amigável para Ciência Homologada */}
                    {statusNorm === "ciencia_registrada" && (
                      <div className="flex items-center gap-1.5 text-sky-600 dark:text-sky-400 text-xs font-medium">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        <span>Ciência homologada na SEFAZ. Aguardando liberação do XML pela Fazenda.</span>
                      </div>
                    )}

                    {statusNorm === "aguardando_mapeamento" && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 sm:p-2.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs font-medium min-w-0">
                          <AlertCircle className="w-4 h-4 shrink-0 text-amber-500" />
                          <span className="truncate">
                            {item.itens_pendentes_mapeamento?.length
                              ? `${item.itens_pendentes_mapeamento.length} bebida(s) nova(s) aguardando vínculo.`
                              : "Bebida nova detectada nesta nota fiscal."}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAbrirModalMapeamento(item)}
                          className="h-7 px-3 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold shrink-0 shadow-sm flex items-center gap-1.5"
                        >
                          <Tag className="w-3 h-3" />
                          Mapear
                        </Button>
                      </div>
                    )}

                    {statusNorm === "erro" && item.mensagem_erro && (
                      <div className="flex items-center gap-1.5 text-rose-500 text-xs font-medium truncate">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{item.mensagem_erro}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                      <span>
                        Bipado às{" "}
                        {item.criado_em.toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        {item.criado_por ? `• ${item.criado_por.split(" ")[0]}` : ""}
                      </span>
                      {item.arquivo_origem && (
                        <span className="text-emerald-500 font-medium truncate max-w-[140px]">
                          ✓ {item.arquivo_origem}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Linha 3: Barra de Ações Mobile (Botões confortáveis para o polegar) */}
                  <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60 dark:border-slate-800/80">
                    {/* Botão Mapear Bebidas (Destaque quando pendente de vínculo) */}
                    {statusNorm === "aguardando_mapeamento" && (
                      <Button
                        size="sm"
                        onClick={() => handleAbrirModalMapeamento(item)}
                        className="h-8 px-3 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-sm flex items-center gap-1.5 flex-1 sm:flex-initial"
                        title="Vincular bebidas ao estoque interno"
                      >
                        <Tag className="w-3.5 h-3.5" />
                        Mapear Bebidas
                      </Button>
                    )}

                    {/* Botão Copiar Chave e Abrir Portal da Fazenda */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAbrirPortalComChave(item.chave_acesso)}
                      className="h-8 px-2.5 text-xs font-medium text-sky-600 dark:text-sky-400 border-sky-500/30 hover:bg-sky-500/10 hover:text-sky-300 transition-colors flex-1 sm:flex-initial"
                      title="Copiar chave de acesso e abrir Portal da Fazenda (Ctrl+V)"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Portal SEFAZ
                    </Button>

                    {/* Botão Subir Arquivo XML baixado */}
                    {statusNorm !== "sucesso" && (
                      <>
                        <input
                          id={`upload-xml-${item.id}`}
                          type="file"
                          accept=".xml"
                          className="hidden"
                          onChange={(e) => handleUploadManualXML(e, item)}
                        />
                        <label
                          htmlFor={`upload-xml-${item.id}`}
                          className="inline-flex items-center justify-center h-8 px-2.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 rounded-md hover:bg-emerald-500/10 hover:text-emerald-300 transition-colors cursor-pointer flex-1 sm:flex-initial"
                          title="Importar arquivo XML baixado"
                        >
                          <Upload className="w-3.5 h-3.5 mr-1.5" />
                          Subir XML
                        </label>
                      </>
                    )}

                    {/* Botão de Reconsultar na SEFAZ */}
                    {(statusNorm === "erro" ||
                      statusNorm === "sucesso" ||
                      statusNorm === "ciencia_registrada") && (
                      <button
                        onClick={() => {
                          setNotaIndividualParaBaixar(item);
                          if (sessaoCertificado?.pfxBase64 || (agentStatus.isLocal && agentStatus.certConfigured)) {
                            executarProcessamentoComCertificado(sessaoCertificado, [item]);
                          } else {
                            setModalCertAberto(true);
                          }
                        }}
                        className="w-8 h-8 flex items-center justify-center text-amber-500 hover:text-amber-400 border border-amber-500/30 rounded-md hover:bg-amber-500/10 transition-colors shrink-0"
                        title="Reconsultar na SEFAZ"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Botão de Excluir */}
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-rose-500 border border-slate-300 dark:border-slate-800 rounded-md hover:bg-rose-500/10 transition-colors shrink-0 ml-auto"
                      title="Remover da Fila"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Modal de Mapeamento de Bebidas da Nota */}
      <ModalMapeamentoNota
        isOpen={modalMapeamentoAberto}
        onClose={() => {
          setModalMapeamentoAberto(false);
          setNotaParaMapear(null);
        }}
        notaInfo={{
          numero: extrairInfoChave(notaParaMapear?.chave_acesso).numero,
          serie: extrairInfoChave(notaParaMapear?.chave_acesso).serie,
          chaveAcesso: notaParaMapear?.chave_acesso,
          cnpjEmitente: notaParaMapear?.cnpj_emitente || notaParaMapear?.cnpjEmitente,
          nomeEmitente: notaParaMapear?.nome_emitente || notaParaMapear?.nomeEmitente,
        }}
        items={notaParaMapear?.itens_pendentes_mapeamento || []}
        estoqueItems={estoqueItems}
        onSalvarSucesso={handleSalvarMapeamentoSucesso}
      />

      {/* Modal Central do Dicionário de Mapeamentos & Auditoria */}
      <DicionarioMapeamentoModal
        isOpen={modalDicionarioAberto}
        onClose={() => setModalDicionarioAberto(false)}
        estoqueItems={estoqueItems}
      />

      {/* Modal de Certificado Digital A1 */}
      <ModalCertificadoSefaz
        isOpen={modalCertAberto}
        onClose={() => {
          setModalCertAberto(false);
          setNotaIndividualParaBaixar(null);
        }}
        onConfirmar={handleConfirmarCertificado}
        isProcessing={isProcessingBatch}
        notasCount={
          notaIndividualParaBaixar
            ? 1
            : fila.filter((f) => {
                const s = normalizarStatus(f);
                return s === "pendente" || s === "erro" || s === "ciencia_registrada";
              }).length || 1
        }
      />
    </Card>
  );
};

export default FilaNotasImportacao;
